import React, { useCallback, useMemo, useRef } from "react";
import cx from "classnames";
import {
  DropdownToggle,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap";
import { Icon, Spinner } from "components";
import { topologyItems } from "objs/_ConfiguratorObjs/Wizard/wizardHelpers";
import { DropDownMenuControlled } from "objs/_ConfiguratorObjs/Wizard/SubComponents/WizardResult/DropdownMenuControlled";
import { PasteConfigurationControls } from "objs/_ConfiguratorObjs/Wizard/SubComponents/WizardResult/PasteConfigurationControls";
import {
  useIsFetching,
  useSelectedTopologyItemId,
  useTopologyOperation,
  useWizardActions,
} from "objs/_ConfiguratorObjs/Wizard/stores/wizardStore";
import styles from "./WizardResult.module.scss";

export const TopologyItem = React.memo(
  ({
    title,
    presentation,
    isSBModule,
    kind,
    isSBItem = false,
    classNames,
    iconName,
    withWhiteTriangle = false,
    id,
    orderCode,
    dummy,
    dragItem,
    isDragPreview = false,
    info,
    menu,
    directlyConnected,
    previouslyAddedElementsCount,
    groupPosition,
    hasChildren = false,
    isWireless = false,
  }) => {
    const topologyOperation = useTopologyOperation();
    const isCopyActionFetching = useIsFetching();
    const selectedTopologyItemId = useSelectedTopologyItemId();
    const { handlePasteAction, setSelectedTopologyItemId } = useWizardActions();

    const ref = useRef(null);

    const offset = useMemo(
      () =>
        directlyConnected && previouslyAddedElementsCount
          ? previouslyAddedElementsCount - directlyConnected.length
          : previouslyAddedElementsCount,
      [directlyConnected, previouslyAddedElementsCount],
    );

    const isPasteEnabled = useMemo(
      () =>
        menu?.find((item) => item?.id === 6 && item?.enabled && item?.highlight)
          ?.highlight,
      [menu],
    );

    const handleClick = useCallback(
      (e) => {
        e.stopPropagation();
        const { kind: action } = topologyOperation || {};
        if (action === "cutComponent") {
          return;
        }
        if (action === "copyConfiguration") {
          if (isPasteEnabled) {
            handlePasteAction(id);
          }
        } else {
          setSelectedTopologyItemId(id);
        }
      },
      [
        topologyOperation,
        isPasteEnabled,
        handlePasteAction,
        id,
        setSelectedTopologyItemId,
      ],
    );

    const { isComponentAction, isConfigAction, isNotAction } = useMemo(() => {
      const { kind: action, item } = topologyOperation || {};
      return {
        isComponentAction:
          (action === "cutComponent" || action === "copyComponent") &&
          item === id,
        isNotAction:
          (action === "cutComponent" || action === "copyComponent") &&
          item !== id,
        isConfigAction: action === "copyConfiguration" && item === id,
      };
    }, [topologyOperation, id]);

    return (
      <div
        className={cx("d-flex flex-column flex-shrink-0 position-relative", {
          [classNames]: !isDragPreview,
          [styles.blurred]: isComponentAction,
        })}
        style={{
          "--additionalHeight": offset ? `${offset * 48}px` : "0px",
          "--top": directlyConnected ? "28px" : "",
          borderLeft: "1px solid blue!important",
          borderRight: "1px solid blue!important",
          "--lineType": isWireless ? "1px dashed #25ace7" : "1px solid #4f5155",
        }}
        id={`topology-item-${id}`}
        onClick={handleClick}
      >
        {isWireless && (
          <div className={styles.topologyItemConnectorWireless}></div>
        )}
        <div className="position-relative w-100 h-100">
          <div
            className={cx("d-flex rounded overflow-hidden", {
              [styles.wirelessNode]: isWireless,
            })}
          >
            <div className="bg-white d-flex align-items-center justify-content-center p-2">
              {dragItem || (
                <Icon
                  prefix="trox"
                  iconName={
                    // TODO: temporary solution for ISH. We should always show FAM icon (not FAME)
                    iconName === "tech-symbol-fame"
                      ? topologyItems[kind]?.iconName
                      : iconName || topologyItems[kind]?.iconName
                  }
                  className="panzoomDisabled icon-32 d-flex align-items-center justify-content-center text-gray-700"
                />
              )}
            </div>
            <div
              style={{ width: "280px" }}
              className={cx(
                "text-white d-flex justify-content-center py-2 ps-2_5 flex-column pe-3 flex-shrink-0 lh-16",
                {
                  "bg-blue-300 text-white":
                    isConfigAction ||
                    isDragPreview ||
                    id === selectedTopologyItemId,
                  [info
                    ? "bg-gray-500"
                    : topologyItems[kind]?.bgColor ||
                      "bg-gray-300 text-gray-700"]:
                    isNotAction ||
                    (id !== selectedTopologyItemId && !isDragPreview),
                },
              )}
            >
              <div className="panzoomDisabled position-absolute top-0 start-0 w-100 h-100" />
              <div className="d-flex">
                <div className="d-flex flex-column">
                  <div
                    className={cx("fw-bolder text-truncate", {
                      "text-blue-800 opacity-100": isComponentAction,
                    })}
                    title={title}
                  >
                    {title}
                  </div>
                  <div
                    className={cx("text-truncate", {
                      "text-warning": presentation === "Dummy",
                    })}
                  >
                    {presentation}
                  </div>
                </div>
                {topologyOperation?.kind === "copyConfiguration" &&
                  isPasteEnabled && (
                    <PasteConfigurationControls title={title} id={id} />
                  )}
              </div>
            </div>
          </div>
          {info && (
            <div
              className="position-absolute"
              style={{
                right: "1.5rem",
                top: "0.4rem",
              }}
            >
              <div ref={ref}>
                <Icon
                  className="ms-2 text-white cursor-pointer"
                  tabIndex={-1}
                  iconName="circle-info"
                  prefix="far"
                />
              </div>
              <UncontrolledTooltip target={ref} placement="top">
                {info}
              </UncontrolledTooltip>
            </div>
          )}
          {!!menu?.length && !isWireless && (
            <UncontrolledDropdown
              className={cx("position-absolute", {
                "text-white":
                  !!topologyItems[kind]?.bgColor ||
                  id === selectedTopologyItemId ||
                  info,
              })}
              direction="left"
              style={{
                right: "0",
                top: "0.25rem",
              }}
            >
              {topologyOperation?.item === id && isCopyActionFetching ? (
                <div
                  className="position-absolute pt-1"
                  style={{
                    top: "40%",

                    right: "30%",
                  }}
                >
                  <Spinner className="loader" size="xs" color={"primary"} />
                </div>
              ) : (
                !topologyOperation && (
                  <DropdownToggle tag="div" className="icon-20 cursor-pointer">
                    <Icon prefix="trox" iconName="ellipsis-vertical-regular" />
                  </DropdownToggle>
                )
              )}
              <DropDownMenuControlled
                {...{
                  id,
                  orderCode,
                  dummy,
                  title,
                  menu,
                  groupPosition,
                  hasChildren,
                }}
              />
            </UncontrolledDropdown>
          )}

          {withWhiteTriangle && (
            <div
              className="position-absolute ratio ratio-1x1"
              style={{
                bottom: "0",
                right: "0",
                width: "1rem",
                background:
                  "linear-gradient(to bottom right, transparent 0%, transparent 50%, white 50%, white 100%)",
                borderRadius: "0 0 2px 0",
              }}
            />
          )}
          {isSBModule && (
            <div className="bg-blue-400 text-white w-100 d-flex justify-content-end p-1 rounded-bottom">
              CB
            </div>
          )}
          {isSBItem && (
            <div
              className="bg-blue-400 w-100 position-absolute rounded-bottom"
              style={{ height: "4px", bottom: "0" }}
            />
          )}
          {directlyConnected?.map((item, key) => (
            <TopologyItem key={key} {...item} />
          ))}
        </div>
      </div>
    );
  },
);
